import type { Product, ProductVariant } from '../types';
import { sanitizeString } from './sanitization';

// SECURITY: Validate attribute key to prevent injection
function isValidAttributeKey(key: string): boolean {
  if (!key || typeof key !== 'string') return false;
  // Only allow alphanumeric and common attribute names (Size, Color, etc.)
  return /^[A-Za-z][A-Za-z0-9_]*$/.test(key) && key.length <= 50;
}

// SECURITY: Sanitize attribute value to prevent XSS
function sanitizeAttributeValue(value: any): string {
  if (!value || typeof value !== 'string') return '';
  // Sanitize and limit length
  return sanitizeString(value, 100) || '';
}

export const getAttr = (v: ProductVariant | undefined, key: string): string => {
  // SECURITY: Validate key before accessing
  if (!isValidAttributeKey(key)) {
    return '';
  }
  const value = v?.attributeValues?.[key] ?? '';
  // SECURITY: Sanitize attribute value before returning
  return sanitizeAttributeValue(value);
};

export function findVariantByAttributes(
  p: Product,
  selected: Record<string, string> // e.g. { Size: "9", Color: "Blue" }
): ProductVariant | null {
  if (p.productType !== "variable" || !p.variants?.length) return null;
  
  // SECURITY: Sanitize and validate all selected attributes before comparison
  const sanitizedSelected: Record<string, string> = {};
  for (const [k, val] of Object.entries(selected)) {
    if (isValidAttributeKey(k)) {
      const sanitizedVal = sanitizeAttributeValue(val);
      if (sanitizedVal) {
        sanitizedSelected[k] = sanitizedVal;
      }
    }
  }
  
  // SECURITY: Only search if we have valid sanitized attributes
  if (Object.keys(sanitizedSelected).length === 0) return null;
  
  return p.variants.find(v =>
    Object.entries(sanitizedSelected).every(([k, val]) => getAttr(v, k) === val)
  ) ?? null;
}

export function getAvailableStock(p: Product, v?: ProductVariant | null) {
  if (p.productType === "variable") return v?.stockQty ?? 0;
  return p.totalStock ?? p.stockQty ?? 0;
}
